import { resetLocalAuthKey, setLocalAuthKey } from '@/common/auth';
import { includesError } from '@/common/errors';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { Logo } from '@/components/logo';
import { useLayoutContext } from '@/contexts';
import { getPossibleRoutes, useCurrentUser } from '@/hooks';
import { Flex } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useId, useMemo, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { CompactButton } from '../../components/buttons/compact';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { LoginForm, LoginFormValues } from './form';
import { useLoginPasswordMutation } from './graphql.generated';
import { useMigratedUserIsLoggingInForTheFirstTime } from './use-first-time-migrated-user/hook';

export function Login() {
	const navigate = useNavigate();
	const { isDesktop } = useLayoutContext();
	const search = useSearch({ from: '/auth/login' });
	const { refetchUser } = useCurrentUser({ skip: true });
	const [loginPassword, { loading }] = useLoginPasswordMutation({ errorPolicy: 'all' });
	const { checkMigratedUserIsLoggingInForTheFirstTime } =
		useMigratedUserIsLoggingInForTheFirstTime();
	const formId = useId();

	const signupButton = useMemo(
		() => (
			<CompactButton leftSection={<IconUser />} onClick={() => navigate({ to: '/signup' })}>
				Sign Up
			</CompactButton>
		),
		[navigate]
	);
	const formRef = useRef<FormikProps<LoginFormValues>>(null);
	const loginButton = useMemo(
		() => (
			<StandardButton type="submit" variant="filled" loading={loading} form={formId}>
				Log In
			</StandardButton>
		),
		[loading, formId]
	);

	const handleSubmit = useCallback(
		async (values: LoginFormValues) => {
			try {
				await resetLocalAuthKey(); // TODO This is a client side solution for what should really be solved on the server
				const { data, errors } = await loginPassword({
					variables: {
						username: values.email.toLowerCase(),
						password: values.password,
					},
				});
				if (data?.loginPassword?.authToken) {
					await setLocalAuthKey(data.loginPassword.authToken);
					await refetchUser();
					const { isMigrated } = await checkMigratedUserIsLoggingInForTheFirstTime();
					const { routingType } = await getPossibleRoutes();
					let destination = '/inform-existing';
					if (!isMigrated) {
						switch (routingType) {
							case 'facility':
								destination = '/facility/home';
								break;
							case 'user':
								destination = '/home';
								break;
							case 'picker':
								destination = '/select-context';
								break;
							case 'no-access':
								destination = '/unauthorised';
								break;
						}
						if (search.redirect) {
							destination = search.redirect;
						}
					}

					// Handle redirect URLs that may be full URLs or contain search params
					try {
						// If destination is a full URL, extract the pathname and search
						if (destination.startsWith('http://') || destination.startsWith('https://')) {
							const url = new URL(destination);
							const searchObject: Record<string, string> = {};

							// Convert URLSearchParams to object
							url.searchParams.forEach((value, key) => {
								searchObject[key] = value;
							});

							navigate({
								to: url.pathname,
								search: Object.keys(searchObject).length > 0 ? searchObject : undefined,
							});
						} else if (destination.includes('?')) {
							// Handle relative URLs with search params
							const [path, searchParams] = destination.split('?');
							const urlParams = new URLSearchParams(searchParams);
							const searchObject: Record<string, string> = {};

							urlParams.forEach((value, key) => {
								searchObject[key] = value;
							});

							navigate({ to: path, search: searchObject });
						} else {
							// Simple path navigation
							navigate({ to: destination });
						}
					} catch (error) {
						// Fallback: if URL parsing fails, try simple navigation
						// eslint-disable-next-line no-console
						console.warn('Failed to parse redirect URL:', destination, error);
						navigate({ to: destination });
					}
				}
				if (includesError(errors, 'Bad Request: Authentication Failed')) {
					formRef.current?.setFieldError('password', 'Username or password is incorrect');
				}
			} catch (error) {
				formRef.current?.setFieldError(
					'email',
					error instanceof Error ? error.message : 'Unknown error'
				);

				// eslint-disable-next-line no-console
				console.error(error);
			}
		},
		[
			loginPassword,
			refetchUser,
			checkMigratedUserIsLoggingInForTheFirstTime,
			navigate,
			search.redirect,
		]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Log In', backTo: '/welcome', children: signupButton }}
			footer={{ children: loginButton }}
		>
			<Flex direction="column" h="100%">
				<ContentPanel justify="center" width="xs" gap="md" flex={1}>
					{isDesktop && <Logo width="300px" height="100px" />}
					<CustomContentSection
						location={ContentSectionLocation.Login}
						placement={ContentSectionPlacement.Top}
					/>
					<LoginForm onSubmit={handleSubmit} formRef={formRef} formId={formId} />
					<CustomContentSection
						location={ContentSectionLocation.Login}
						placement={ContentSectionPlacement.Middle}
					/>
					<CustomContentSection
						location={ContentSectionLocation.Login}
						placement={ContentSectionPlacement.Bottom}
					/>
				</ContentPanel>
				{isDesktop && (
					<Flex w="100%" justify="center" align="center">
						<Flex w={220}>{loginButton}</Flex>
					</Flex>
				)}
			</Flex>
		</PrimaryLayout>
	);
}
